import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { publicAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  ClockIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const contactMutation = useMutation({
    mutationFn: publicAPI.submitContactForm,
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || t.contact.sentSuccess);
        reset();
      } else {
        toast.error(data.message || t.contact.sendFailed);
      }
      setIsSubmitting(false);
    },
    onError: (error) => {
      const message = error.response?.data?.message || t.contact.sendFailed;
      toast.error(message);
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data) => {
    setIsSubmitting(true);
    contactMutation.mutate(data);
  };

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: t.contact.berlinHQ,
      details: ['+49 677 442 0901'],
      description: t.contact.berlinDesc
    },
    {
      icon: PhoneIcon,
      title: t.contact.usaOffice,
      details: ['+1 213-477-4806'],
      description: t.contact.usaDesc
    },
    {
      icon: EnvelopeIcon,
      title: t.contact.email,
      details: ['support@dutchcargoexpress.com'],
      description: t.contact.emailDesc
    },
    {
      icon: ClockIcon,
      title: t.contact.businessHours,
      details: [t.contact.hoursDetail],
      description: t.contact.hoursDesc
    }
  ];

  const faqs = [
    { question: t.contact.faq1Q, answer: t.contact.faq1A },
    { question: t.contact.faq2Q, answer: t.contact.faq2A },
    { question: t.contact.faq3Q, answer: t.contact.faq3A },
    { question: t.contact.faq4Q, answer: t.contact.faq4A },
    { question: t.contact.faq5Q, answer: t.contact.faq5A },
    { question: t.contact.faq6Q, answer: t.contact.faq6A }
  ];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Hero with background image */}
      <section className="relative text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1920&q=80"
            alt="Customer service team at work"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3A]/90 via-[#123B63]/80 to-[#123B63]/70"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-24 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-4 sm:mb-5 shadow-lg">
            <ChatBubbleLeftRightIcon className="h-6 w-6 sm:h-7 sm:h-7 md:h-8 md:w-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-3 sm:mb-4 leading-tight">
            {t.contact.title}
          </h1>
          <p className="text-sm sm:text-base md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed px-2">
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Quick contact cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-10 relative z-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {contactInfo.map((item, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg border border-[#E2E8F0] p-4 sm:p-5 text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full mb-3">
                <item.icon className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF6B00]" />
              </div>
              <h3 className="font-bold text-[#0B1F3A] mb-1 text-xs sm:text-sm md:text-base">{item.title}</h3>
              {item.details.map((detail, detailIndex) => (
                <p key={detailIndex} className="text-[#17202A] text-xs sm:text-sm break-all">{detail}</p>
              ))}
              <p className="text-[10px] sm:text-xs text-[#64748B] mt-1">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Contact Form */}
          <div>
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6 md:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F3A] mb-2">
                {t.contact.sendMessage}
              </h2>
              <p className="text-[#64748B] mb-5 sm:mb-6 text-sm">
                {t.contact.formDesc}
              </p>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t.contact.fullName}
                    </label>
                    <input
                      id="name"
                      type="text"
                      {...register('name', { required: t.contact.nameRequired })}
                      className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 text-sm ${errors.name ? 'border-[#DC2626]' : 'border-[#E2E8F0] focus:border-[#FF6B00]'}`}
                      placeholder={t.contact.fullNamePlaceholder}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t.contact.emailLabel}
                    </label>
                    <input
                      id="email"
                      type="email"
                      {...register('email', { 
                        required: t.contact.emailRequired,
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t.contact.emailInvalid
                        }
                      })}
                      className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 text-sm ${errors.email ? 'border-[#DC2626]' : 'border-[#E2E8F0] focus:border-[#FF6B00]'}`}
                      placeholder={t.contact.emailPlaceholder}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t.contact.subject}
                  </label>
                  <select
                    id="subject"
                    {...register('subject', { required: t.contact.subjectRequired })}
                    className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 text-sm ${errors.subject ? 'border-[#DC2626]' : 'border-[#E2E8F0] focus:border-[#FF6B00]'}`}
                  >
                    <option value="">{t.contact.selectSubject}</option>
                    <option value="General Inquiry">{t.contact.generalInquiry}</option>
                    <option value="Tracking Issue">{t.contact.trackingIssue}</option>
                    <option value="Shipping Question">{t.contact.shippingQuestion}</option>
                    <option value="Billing Question">{t.contact.billingQuestion}</option>
                    <option value="Damaged Package">{t.contact.damagedPackage}</option>
                    <option value="Lost Package">{t.contact.lostPackage}</option>
                    <option value="Complaint">{t.contact.complaint}</option>
                    <option value="Compliment">{t.contact.compliment}</option>
                    <option value="Other">{t.contact.other}</option>
                  </select>
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="trackingNumber" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t.contact.trackingOptional}
                  </label>
                  <input
                    id="trackingNumber"
                    type="text"
                    {...register('trackingNumber')}
                    className="w-full px-4 py-2.5 border-2 border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-orange-200 text-sm"
                    placeholder="SC20231201123456"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t.contact.message}
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    {...register('message', { 
                      required: t.contact.messageRequired,
                      minLength: {
                        value: 10,
                        message: t.contact.messageTooShort
                      }
                    })}
                    className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 text-sm resize-none ${errors.message ? 'border-[#DC2626]' : 'border-[#E2E8F0] focus:border-[#FF6B00]'}`}
                    placeholder={t.contact.messagePlaceholder}
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 sm:py-3.5 bg-[#FF6B00] hover:bg-[#e65f00] text-white font-semibold rounded-lg transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>{t.contact.sending}</span>
                    </>
                  ) : (
                    <>
                      <EnvelopeIcon className="h-5 w-5" />
                      <span>{t.contact.send}</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Right column — Map + info image */}
          <div className="space-y-4 sm:space-y-6">
            {/* Map */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-[#0B1F3A] text-white px-4 sm:px-5 py-2.5 sm:py-3 flex items-center justify-between">
                <span className="font-bold text-sm sm:text-base">{t.contact.ourLocation}</span>
                <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B00]" />
              </div>
              <div className="relative w-full h-[220px] bg-gray-100">

                <iframe
                  title="Office Location"
                  width="100%"
                  height="220"
                  frameBorder="0"
                  scrolling="no"
                  
                  src="https://maps.google.com/maps?q=Berlin%2C%20Germany&t=&z=10&ie=UTF8&iwloc=&output=embed"
                  className="w-full relative z-10 bg-white"
                />
              </div>
            </div>

            {/* Info image card */}
            <div className="relative rounded-xl overflow-hidden shadow-lg h-48 sm:h-56 md:h-64">
              <img
                src="https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=900&q=80"
                alt="Cargo logistics operations"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/90 via-[#0B1F3A]/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                <h3 className="text-white text-base sm:text-lg font-bold mb-1">{t.contact.globalHub}</h3>
                <p className="text-gray-300 text-xs sm:text-sm">{t.contact.globalHubDesc}</p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 sm:mt-16">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-block bg-orange-100 text-[#FF6B00] px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-3">
              {t.contact.helpCenter}
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0B1F3A] mb-2">
              {t.contact.faq}
            </h2>
            <p className="text-sm sm:text-base text-[#64748B]">
              {t.contact.faqDesc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-4 sm:p-5 md:p-6 hover:shadow-md transition-shadow">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold text-[#0B1F3A] mb-2">
                  {faq.question}
                </h3>
                <p className="text-[#64748B] text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
